import type React from "react"

interface CenteredLayoutProps {
  children: React.ReactNode
  /** Maximum width of the content card */
  maxWidth?: "sm" | "md" | "lg"
  /** Whether to show the card container */
  showCard?: boolean
  /** Background color variant */
  background?: "light" | "white"
}

/**
 * CenteredLayout - A full-screen centered layout for auth pages, error states, and hero sections.
 *
 * Use this component for:
 * - Login/authentication pages
 * - Error/rejection pages
 * - Tenant selection pages
 * - Any full-screen centered content
 */
const CenteredLayout: React.FC<CenteredLayoutProps> = ({
  children,
  maxWidth = "md",
  showCard = true,
  background = "light",
}) => {
  const maxWidthStyles = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
  }

  const backgroundStyles = {
    light: "bg-sand-50",
    white: "bg-white",
  }

  return (
    <div
      className={`
        min-h-screen flex flex-col items-center justify-center p-4
        ${backgroundStyles[background]}
      `}
    >
      {showCard ? (
        <div
          className={`
            bg-white rounded-xl shadow-lg p-8 w-full
            ${maxWidthStyles[maxWidth]}
          `}
        >
          {children}
        </div>
      ) : (
        <div className={`w-full ${maxWidthStyles[maxWidth]}`}>{children}</div>
      )}
    </div>
  )
}

export default CenteredLayout
